// A form region is a part of a form that auto-save keeps and restores as a whole: the Goals v3
// session data on a visit note, the goals editor on a Plan of Care or evaluation, and the goal
// decisions on a discharge. The rest of the form restores field by field, finding each field by
// its id, which cannot bring back a row the page added while the user worked, a value kept in a
// hidden input or an Impulse select's pick. A region is kept as what it would submit instead, and
// its own controller rebuilds it from that copy (see traverseDOM and restoreRegion in
// auto_save_controller.js).
//
// A region's element carries an id, data-auto-save-region with the name prefix its fields share
// (e.g. "visit_note[datum][<control id>][goals]"), and an action for auto-save:restore, whose
// detail.fields is the copy.

const UNSUBMITTED_TYPES = ['button', 'submit', 'reset', 'image', 'file'];

function underPrefix(name, prefix) {
  return !prefix || name === prefix || name.startsWith(`${prefix}[`);
}

function isCheckable(field) {
  return field instanceof HTMLInputElement && ['checkbox', 'radio'].includes(field.type);
}

// The region's fields a form could submit under the prefix, in page order. A field inside a
// <template> is not on the page, and querySelectorAll never reaches it.
function namedFields(region, prefix) {
  return Array.from(region.querySelectorAll('input[name], select[name], textarea[name]')).filter(
    (field) =>
      underPrefix(field.name, prefix) && !(field instanceof HTMLInputElement && UNSUBMITTED_TYPES.includes(field.type))
  );
}

/**
 * What the region would submit under the prefix, in page order: each enabled field's name and
 * value, and a checkbox or radio only while it is checked.
 *
 * @param {HTMLElement} region
 * @param {string} prefix
 * @returns {Array<[string, string]>}
 */
export function regionFields(region, prefix) {
  return namedFields(region, prefix).flatMap((field) => {
    if (field.disabled || (isCheckable(field) && !field.checked)) return [];
    if (field instanceof HTMLSelectElement && field.multiple) {
      return Array.from(field.selectedOptions, (option) => [field.name, option.value]);
    }
    return [[field.name, field.value]];
  });
}

/**
 * The bracketed parts of a field name after the prefix: "r[g1][entries][e1][correct]" with the
 * prefix "r" reads ["g1", "entries", "e1", "correct"].
 *
 * @param {string} name
 * @param {string} prefix
 * @returns {string[]}
 */
export function nameSegments(name, prefix) {
  const rest = prefix && name.startsWith(prefix) ? name.slice(prefix.length) : name;
  return Array.from(rest.matchAll(/\[([^\]]*)\]/g), (match) => match[1]);
}

/**
 * Whether a key read from a copy can go into markup, as a row or line built from a template does:
 * letters, digits, underscores and hyphens, which covers every key the page makes (goal ids and
 * the "new-" keys) and nothing that could close an attribute. A copy is built from the page, and
 * a field name can carry whatever a crafted save put in the note's data.
 *
 * @param {string} key
 * @returns {boolean}
 */
export function isPlainKey(key) {
  return typeof key === 'string' && /^[\w-]+$/.test(key);
}

/**
 * Puts a value in one field. An Impulse select keeps its value in a hidden input and shows the
 * chosen option's words in its box, so it takes the value as its pick. It is written here rather
 * than through the element's own setValue, which fails until the element has started, and a line
 * added a moment ago has not. Nothing is announced, so no change handler runs.
 *
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
 * @param {string} value
 */
export function writeField(field, value) {
  const select = field.matches('input[data-behavior="hidden-field"]') ? field.closest('awc-autocomplete') : null;
  if (!select) {
    field.value = value;
    return;
  }

  const option = Array.from(select.querySelectorAll('[role="option"]')).find((o) => o.getAttribute('value') === value);
  const text = value !== '' && option ? option.dataset.text || option.textContent.trim() : '';
  field.value = value;
  field.setAttribute('data-text', text);
  const input = select.querySelector('.awc-autocomplete-input');
  if (input) input.value = text;
  select.classList.toggle('awc-autocomplete--selected', value !== '');
}

/**
 * Writes a copy made by regionFields back into the region. Each field the copy names takes its
 * value (when a name is on several fields, the ones that submit take the values first, in order)
 * and each checkbox or radio under a name the copy holds is checked when the copy lists its value.
 * A field the copy does not name keeps what it holds.
 *
 * @param {HTMLElement} region
 * @param {Array<[string, string]>} fields
 * @param {string} prefix
 */
export function fillRegion(region, fields, prefix) {
  const copied = new Map();
  fields.forEach(([name, value]) => {
    if (!underPrefix(name, prefix)) return;
    if (!copied.has(name)) copied.set(name, []);
    copied.get(name).push(value);
  });

  const byName = new Map();
  namedFields(region, prefix).forEach((field) => {
    if (!byName.has(field.name)) byName.set(field.name, []);
    byName.get(field.name).push(field);
  });

  byName.forEach((group, name) => {
    const values = copied.get(name);
    if (!values) return;

    group.filter(isCheckable).forEach((box) => {
      box.checked = values.includes(box.value);
    });

    const others = group.filter((field) => !isCheckable(field));
    const submitting = others.filter((field) => !field.disabled);
    const pool = submitting.length > 0 ? [...submitting, ...others.filter((field) => field.disabled)] : others;
    values.slice(0, pool.length).forEach((value, index) => writeField(pool[index], value));
  });
}

/**
 * Orders sibling elements as given, in the places they hold now: the first of those places takes
 * the first element, and whatever sits between them stays where it is.
 *
 * @param {HTMLElement[]} elements
 */
export function placeInOrder(elements) {
  if (elements.length < 2) return;
  const places = Array.from(elements[0].parentNode.children).filter((element) => elements.includes(element));
  if (places.length !== elements.length || places.every((element, index) => element === elements[index])) return;

  const markers = places.map((element) => {
    const marker = document.createComment('');
    element.replaceWith(marker);
    return marker;
  });
  markers.forEach((marker, index) => marker.replaceWith(elements[index]));
}
